/**
 * Single source of truth for the full Tailwind/shadcn-compatible theme color
 * token surface — shared by the tenant-dashboard Colors page (schema + form),
 * the builder canvas preview, and the public region theme loader so all three
 * agree on key names, group layout, CSS variable names and per-mode defaults.
 */

export const THEME_COLOR_TOKEN_KEYS = [
	"primary",
	"primary-foreground",
	"secondary",
	"secondary-foreground",
	"accent",
	"accent-foreground",
	"background",
	"foreground",
	"card",
	"card-foreground",
	"popover",
	"popover-foreground",
	"muted",
	"muted-foreground",
	"border",
	"input",
	"ring",
	"sidebar",
	"sidebar-foreground",
	"sidebar-primary",
	"sidebar-accent",
	"sidebar-border",
	"success",
	"success-foreground",
	"warning",
	"warning-foreground",
	"destructive",
	"destructive-foreground",
	"info",
	"info-foreground",
] as const;

export type ThemeColorTokenKey = (typeof THEME_COLOR_TOKEN_KEYS)[number];

export type ThemeColorTokenGroup = {
	id: string;
	label: string;
	description: string;
	keys: ThemeColorTokenKey[];
};

export const THEME_COLOR_TOKEN_GROUPS: ThemeColorTokenGroup[] = [
	{
		id: "brand",
		label: "Brand",
		description:
			"Primary, secondary and accent colors used for buttons, links and highlights across your public site.",
		keys: [
			"primary",
			"primary-foreground",
			"secondary",
			"secondary-foreground",
			"accent",
			"accent-foreground",
		],
	},
	{
		id: "surfaces",
		label: "Surfaces",
		description:
			"Page, card and popover surfaces shown to visitors, with their matching text colors.",
		keys: [
			"background",
			"foreground",
			"card",
			"card-foreground",
			"popover",
			"popover-foreground",
		],
	},
	{
		id: "neutrals",
		label: "Neutrals & controls",
		description:
			"Muted backgrounds, borders, inputs and focus rings used across your public site.",
		keys: ["muted", "muted-foreground", "border", "input", "ring"],
	},
	{
		id: "sidebar",
		label: "Site sidebar",
		description:
			"Colors for public website side navigation, active links and dividers.",
		keys: [
			"sidebar",
			"sidebar-foreground",
			"sidebar-primary",
			"sidebar-accent",
			"sidebar-border",
		],
	},
	{
		id: "state",
		label: "State colors",
		description:
			"Feedback banners shown to visitors — each state has its own paired text color.",
		keys: [
			"success",
			"success-foreground",
			"warning",
			"warning-foreground",
			"destructive",
			"destructive-foreground",
			"info",
			"info-foreground",
		],
	},
];

export const LIGHT_THEME_COLOR_DEFAULTS: Record<ThemeColorTokenKey, string> = {
	primary: "#6366f1",
	"primary-foreground": "#ffffff",
	secondary: "#f1f5f9",
	"secondary-foreground": "#0f172a",
	accent: "#a855f7",
	"accent-foreground": "#ffffff",
	background: "#ffffff",
	foreground: "#0f172a",
	card: "#ffffff",
	"card-foreground": "#0f172a",
	popover: "#ffffff",
	"popover-foreground": "#0f172a",
	muted: "#f1f5f9",
	"muted-foreground": "#64748b",
	border: "#e2e8f0",
	input: "#e2e8f0",
	ring: "#6366f1",
	sidebar: "#fafafa",
	"sidebar-foreground": "#0f172a",
	"sidebar-primary": "#6366f1",
	"sidebar-accent": "#f1f5f9",
	"sidebar-border": "#e2e8f0",
	success: "#10b981",
	"success-foreground": "#ffffff",
	warning: "#f59e0b",
	"warning-foreground": "#1c1917",
	destructive: "#ef4444",
	"destructive-foreground": "#ffffff",
	info: "#0ea5e9",
	"info-foreground": "#ffffff",
};

export const DARK_THEME_COLOR_DEFAULTS: Record<ThemeColorTokenKey, string> = {
	primary: "#818cf8",
	"primary-foreground": "#0f172a",
	secondary: "#1e293b",
	"secondary-foreground": "#f8fafc",
	accent: "#c084fc",
	"accent-foreground": "#0f172a",
	background: "#0f172a",
	foreground: "#f8fafc",
	card: "#1e293b",
	"card-foreground": "#f8fafc",
	popover: "#1e293b",
	"popover-foreground": "#f8fafc",
	muted: "#1e293b",
	"muted-foreground": "#94a3b8",
	border: "#334155",
	input: "#334155",
	ring: "#818cf8",
	sidebar: "#0b1120",
	"sidebar-foreground": "#f8fafc",
	"sidebar-primary": "#818cf8",
	"sidebar-accent": "#1e293b",
	"sidebar-border": "#334155",
	success: "#10b981",
	"success-foreground": "#052e1c",
	warning: "#f59e0b",
	"warning-foreground": "#1c1917",
	destructive: "#ef4444",
	"destructive-foreground": "#ffffff",
	info: "#0ea5e9",
	"info-foreground": "#0c1a26",
};

/** "primary-foreground" → "Primary Foreground"; "sidebar-primary" → "Sidebar Primary". Generic Title Case helper — swatch tooltips use the curated `THEME_COLOR_TOKEN_LABELS` instead; kept exported for any other generic-humanization need. */
export function formatThemeColorTokenLabel(key: string): string {
	return key
		.split("-")
		.map((part) => part.charAt(0).toUpperCase() + part.slice(1))
		.join(" ");
}

/** Curated, CMS-friendly labels for the Colors form — deliberately not a literal humanization of the key (e.g. "destructive" → "Error"). */
export const THEME_COLOR_TOKEN_LABELS: Record<ThemeColorTokenKey, string> = {
	primary: "Primary",
	"primary-foreground": "Primary text",
	secondary: "Secondary",
	"secondary-foreground": "Secondary text",
	accent: "Accent",
	"accent-foreground": "Accent text",
	background: "Page background",
	foreground: "Body text",
	card: "Card",
	"card-foreground": "Card text",
	popover: "Popover",
	"popover-foreground": "Popover text",
	muted: "Muted surface",
	"muted-foreground": "Muted text",
	border: "Border",
	input: "Input",
	ring: "Focus ring",
	sidebar: "Sidebar",
	"sidebar-foreground": "Sidebar text",
	"sidebar-primary": "Sidebar primary",
	"sidebar-accent": "Sidebar accent",
	"sidebar-border": "Sidebar border",
	success: "Success",
	"success-foreground": "Success text",
	warning: "Warning",
	"warning-foreground": "Warning text",
	destructive: "Error",
	"destructive-foreground": "Error text",
	info: "Info",
	"info-foreground": "Info text",
};

/** `background`/`foreground` alias to the short-form `--tt-color-bg`/`--tt-color-fg` for backward compatibility; every other key is `--tt-color-<key>`. */
export function themeColorCssVar(key: ThemeColorTokenKey): string {
	if (key === "background") {
		return "--tt-color-bg";
	}
	if (key === "foreground") {
		return "--tt-color-fg";
	}
	return `--tt-color-${key}`;
}

/**
 * Historic / mistaken CSS variable names used by form presets and help text.
 * Always point at the real tokens so public CSS does not silently fall back
 * (`--tt-color-muted` is a surface, not body text; `--tt-color-primary-fg`
 * was never emitted).
 */
export function themeColorCssVarAliasDeclarations(): string[] {
	return [
		"--tt-color-primary-fg: var(--tt-color-primary-foreground);",
		"--tt-color-muted-bg: var(--tt-color-muted);",
	];
}

/** `themeColorCssVar(key)` wrapped as a `var(...)` reference — the value a Theme Colors pick persists so bound elements track live Branding changes instead of freezing a resolved hex/gradient. */
export function themeColorCssVarRef(key: ThemeColorTokenKey): string {
	return `var(${themeColorCssVar(key)})`;
}

const THEME_COLOR_VAR_TO_KEY: ReadonlyMap<string, ThemeColorTokenKey> = new Map(
	THEME_COLOR_TOKEN_KEYS.map((key) => [themeColorCssVar(key), key])
);

const CSS_VAR_REF_RE = /^var\(\s*(--[a-z0-9-]+)\s*\)$/i;

/** Reverses `themeColorCssVarRef`: `"var(--tt-color-bg)"` → `"background"`. Returns `null` for anything else (hex, gradient, an unrecognized `--tt-*` var, or a malformed `var()`). */
export function parseThemeColorCssVarKey(
	value: string
): ThemeColorTokenKey | null {
	const match = CSS_VAR_REF_RE.exec(value.trim());
	if (!match?.[1]) {
		return null;
	}
	return THEME_COLOR_VAR_TO_KEY.get(match[1].toLowerCase()) ?? null;
}

export type ThemeColorSwatch = {
	value: string;
	label?: string;
	/** Which Site Colours mode this token was read from — drives the ColorPicker's Theme Colors Light/Dark toggle. */
	themeMode?: "light" | "dark";
	/** `var(--tt-color-<key>)` — persisted on pick instead of `value` so the element tracks live Branding changes (see `themeColorCssVarRef`). */
	varValue?: string;
};

/**
 * One swatch per token key (per mode) that has a non-empty value — feeds a
 * ColorPicker's "Theme Colors" palette group. Deliberately NOT deduped by
 * hex: Site Colours kit derivation makes many tokens share the same hex
 * (e.g. `card`/`popover`/`muted` often match), and collapsing to unique
 * values hid every key after the first. Tooltip labels (`THEME_COLOR_TOKEN_LABELS`)
 * already distinguish same-hex swatches, and each swatch's `themeMode` lets
 * the ColorPicker filter by its Light/Dark toggle instead of a `· Light`/
 * `· Dark` label suffix. Shared by the dashboard Colors form and the builder
 * inspector so both list the same set.
 */
export function buildThemeColorSwatches(
	light: Partial<Record<ThemeColorTokenKey, string | null>>,
	dark: Partial<Record<ThemeColorTokenKey, string | null>>
): ThemeColorSwatch[] {
	const swatches: ThemeColorSwatch[] = [];
	const modeStates: readonly [
		"light" | "dark",
		Partial<Record<ThemeColorTokenKey, string | null>>,
	][] = [
		["light", light],
		["dark", dark],
	];
	for (const [themeMode, state] of modeStates) {
		for (const key of THEME_COLOR_TOKEN_KEYS) {
			const raw = (state[key] ?? "").trim();
			if (!raw) {
				continue;
			}
			swatches.push({
				value: raw,
				label: THEME_COLOR_TOKEN_LABELS[key],
				themeMode,
				varValue: themeColorCssVarRef(key),
			});
		}
	}
	return swatches;
}

/* ────────────────────────────────────────────────────────────────────── */
/* Site colours kit — a small, Wix-style set of ~8 fields non-technical     */
/* editors see by default. Every one of the 30 tokens above is             */
/* deterministically derived from it; Advanced still edits tokens directly. */
/* ────────────────────────────────────────────────────────────────────── */

export const SITE_COLOR_KIT_KEYS = [
	"primary",
	"secondary",
	"accent",
	"headline",
	"body",
	"darkNeutral",
	"lightNeutral",
	"white",
] as const;

export type SiteColorKitKey = (typeof SITE_COLOR_KIT_KEYS)[number];

export type SiteColorKit = Record<SiteColorKitKey, string>;

export type SiteColorKitFieldDef = {
	key: SiteColorKitKey;
	label: string;
	help: string;
};

/** Plain-English fields for the Simple "Site colours" editor. Labels match `THEME_COLOR_TOKEN_LABELS` for the token each field drives, so Simple and Advanced never disagree on what a colour is called — help text stays short and visitor-facing rather than mirroring the Advanced description verbatim. */
export const SITE_COLOR_KIT_FIELDS: SiteColorKitFieldDef[] = [
	{
		key: "primary",
		label: "Primary",
		help: "Your main brand colour. Used by most key actions on the site.",
	},
	{
		key: "secondary",
		label: "Secondary",
		help: "Secondary brand colour. Used for secondary actions and supporting UI.",
	},
	{
		key: "accent",
		label: "Accent",
		help: "Contrasting highlight for attention (use sparingly).",
	},
	{
		key: "headline",
		label: "Body text",
		help: "Main text colour for the site — headings and body inherit this for now. Typography can override per heading/paragraph later.",
	},
	{
		key: "body",
		label: "Muted text",
		help: "Captions, metadata and supporting copy.",
	},
	{
		key: "darkNeutral",
		label: "Sidebar",
		help: "Background for the footer, side navigation and other dark sections.",
	},
	{
		key: "lightNeutral",
		label: "Muted surface",
		help: "Soft / alternating section backgrounds.",
	},
	{
		key: "white",
		label: "Page background",
		help: "Main page canvas colour (often light in Light mode, dark in Dark mode).",
	},
];

type RGB = { r: number; g: number; b: number };

/** Parses `#rgb` / `#rrggbb` (alpha ignored) into 0–255 channels. Returns `null` for anything else (gradients, css vars, named colors) so callers can fall back gracefully. */
function parseHexColor(value: string): RGB | null {
	const hex = value.trim().replace(/^#/, "");
	if (!/^[0-9a-f]{3}$|^[0-9a-f]{6}$|^[0-9a-f]{8}$/i.test(hex)) {
		return null;
	}
	const full =
		hex.length === 3
			? hex
					.split("")
					.map((c) => c + c)
					.join("")
			: hex.slice(0, 6);
	const int = Number.parseInt(full, 16);
	return {
		r: Math.floor(int / 65_536) % 256,
		g: Math.floor(int / 256) % 256,
		b: int % 256,
	};
}

function clampChannel(value: number): number {
	return Math.min(255, Math.max(0, Math.round(value)));
}

function rgbToHex({ r, g, b }: RGB): string {
	const toHex = (c: number) => clampChannel(c).toString(16).padStart(2, "0");
	return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/** Linear-blends two colors; `t=0` returns `a`, `t=1` returns `b`. Falls back to `a` unchanged if either value isn't a parseable hex color. */
export function mixHexColors(a: string, b: string, t: number): string {
	const rgbA = parseHexColor(a);
	const rgbB = parseHexColor(b);
	if (!(rgbA && rgbB)) {
		return a;
	}
	const mix = (x: number, y: number) => x + (y - x) * t;
	return rgbToHex({
		r: mix(rgbA.r, rgbB.r),
		g: mix(rgbA.g, rgbB.g),
		b: mix(rgbA.b, rgbB.b),
	});
}

function relativeLuminance({ r, g, b }: RGB): number {
	const channel = (c: number) => {
		const s = c / 255;
		return s <= 0.039_28 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
	};
	return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** WCAG relative-luminance contrast ratio: 1 (no contrast) to 21 (max). Returns 1 for unparseable input rather than throwing. */
export function contrastRatio(a: string, b: string): number {
	const rgbA = parseHexColor(a);
	const rgbB = parseHexColor(b);
	if (!(rgbA && rgbB)) {
		return 1;
	}
	const lumA = relativeLuminance(rgbA);
	const lumB = relativeLuminance(rgbB);
	const lighter = Math.max(lumA, lumB);
	const darker = Math.min(lumA, lumB);
	return (lighter + 0.05) / (darker + 0.05);
}

const CONTRAST_INK = "#0f172a";
const CONTRAST_PAPER = "#ffffff";
/** Below this luminance a color reads as "very dark" — steers the auto-derived focus ring toward `secondary` instead of a near-invisible dark `primary`. */
const VERY_DARK_LUMINANCE_THRESHOLD = 0.06;

/**
 * Picks whichever of near-black ink or white contrasts better against
 * `background` — used to auto-derive every `*-foreground` pair from the kit.
 * Not a full AA/AAA solver, but sufficient for brand primaries/secondaries/
 * accents, which are rarely borderline; small deviations from a tenant's
 * previously hand-picked foreground are expected and documented, not a bug.
 */
export function autoContrastForeground(background: string): string {
	const withInk = contrastRatio(background, CONTRAST_INK);
	const withPaper = contrastRatio(background, CONTRAST_PAPER);
	return withPaper >= withInk ? CONTRAST_PAPER : CONTRAST_INK;
}

function isVeryDarkColor(value: string): boolean {
	const rgb = parseHexColor(value);
	return rgb ? relativeLuminance(rgb) < VERY_DARK_LUMINANCE_THRESHOLD : false;
}

/** `sidebar` in the dark defaults sits *below* the page background (a distinct deep band); `foreground` in the light defaults is dark ink and light themes have no naturally-dark surface token. Picking whichever of the two is darker recovers the right source for `darkNeutral` in both modes without an explicit mode flag. */
function darkerOf(a: string, b: string): string {
	const rgbA = parseHexColor(a);
	const rgbB = parseHexColor(b);
	if (!(rgbA && rgbB)) {
		return a;
	}
	return relativeLuminance(rgbA) <= relativeLuminance(rgbB) ? a : b;
}

/**
 * Projects the full 30-token set down to the 8 fields a non-technical editor
 * sees. Most fields are a direct 1:1 read of an existing token; `darkNeutral`
 * has no dedicated token in either mode's defaults, so it's recovered via
 * `darkerOf` (see above).
 */
export function extractSiteColorKitFromTokens(
	tokens: Record<ThemeColorTokenKey, string>
): SiteColorKit {
	return {
		primary: tokens.primary,
		secondary: tokens.secondary,
		accent: tokens.accent,
		headline: tokens.foreground,
		body: tokens["muted-foreground"],
		darkNeutral: darkerOf(tokens.sidebar, tokens.foreground),
		lightNeutral: tokens.muted,
		white: tokens.background,
	};
}

/**
 * Derives the full 30-token set for one mode from the 8-field kit. Every
 * `*-foreground` pair is contrast-derived (`autoContrastForeground`), never
 * copied from the kit directly. State tokens (success/warning/destructive/
 * info + their foregrounds) intentionally keep the mode's built-in defaults —
 * brand colors should never leak into error/success feedback.
 *
 * `card`/`popover` use `white` in light mode but `lightNeutral` in dark mode:
 * dark themes need an "elevated" surface visually lighter than the page
 * background for cards to read as distinct from the canvas, matching how
 * `DARK_THEME_COLOR_DEFAULTS` is actually structured (card/popover/muted all
 * share one elevated tone, separate from `background`).
 */
export function deriveThemeTokensFromKit(
	kit: SiteColorKit,
	mode: "light" | "dark"
): Record<ThemeColorTokenKey, string> {
	const stateDefaults =
		mode === "light" ? LIGHT_THEME_COLOR_DEFAULTS : DARK_THEME_COLOR_DEFAULTS;
	const surface = mode === "dark" ? kit.lightNeutral : kit.white;
	const border = mixHexColors(kit.lightNeutral, kit.body, 0.2);
	const ring = isVeryDarkColor(kit.primary) ? kit.secondary : kit.primary;
	const sidebar = mode === "dark" ? kit.darkNeutral : kit.lightNeutral;
	// Washed from `lightNeutral` (the "elevated" tone), not the sidebar's own
	// background — in dark mode `sidebar` is a very deep band, and washing
	// straight off it barely moves; washing off the lighter elevated tone
	// keeps the hover/secondary sidebar item legibly different.
	const sidebarAccent = mixHexColors(kit.lightNeutral, kit.primary, 0.12);

	return {
		primary: kit.primary,
		"primary-foreground": autoContrastForeground(kit.primary),
		secondary: kit.secondary,
		"secondary-foreground": autoContrastForeground(kit.secondary),
		accent: kit.accent,
		"accent-foreground": autoContrastForeground(kit.accent),
		background: kit.white,
		foreground: kit.headline,
		card: surface,
		"card-foreground": kit.headline,
		popover: surface,
		"popover-foreground": kit.headline,
		muted: kit.lightNeutral,
		"muted-foreground": kit.body,
		border,
		input: border,
		ring,
		sidebar,
		"sidebar-foreground": kit.headline,
		"sidebar-primary": kit.primary,
		"sidebar-accent": sidebarAccent,
		"sidebar-border": border,
		success: stateDefaults.success,
		"success-foreground": stateDefaults["success-foreground"],
		warning: stateDefaults.warning,
		"warning-foreground": stateDefaults["warning-foreground"],
		destructive: stateDefaults.destructive,
		"destructive-foreground": stateDefaults["destructive-foreground"],
		info: stateDefaults.info,
		"info-foreground": stateDefaults["info-foreground"],
	};
}

/** Seed kits for brand-new tenants (or legacy rows with only the flat 5-key shape) — extracted once from the runtime defaults so Simple always starts from an already-round-tripped, coherent kit. */
export const LIGHT_SITE_COLOR_KIT_DEFAULTS: SiteColorKit =
	extractSiteColorKitFromTokens(LIGHT_THEME_COLOR_DEFAULTS);
export const DARK_SITE_COLOR_KIT_DEFAULTS: SiteColorKit =
	extractSiteColorKitFromTokens(DARK_THEME_COLOR_DEFAULTS);
